import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Loader, TrendingUp, Clock } from 'lucide-react';
import { http } from '../../lib/api';

export default function HistorialHorasModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null); // null = últimos 6 meses

  const formatHHMM = (decimal) => {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
    fetchHistorial();
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedYear]);

  const fetchHistorial = async () => {
    try {
      setLoading(true);
      let url = '/stats/historial-horas?meses=6';
      if (selectedYear) {
        url += `&anio=${selectedYear}`;
      }
      const res = await http.get(url);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching historial:", err);
      setError("No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              Historial de Consumo
            </h2>
            <p className="text-slate-500 mt-1 ml-1">
              Análisis {selectedYear ? `anual ${selectedYear}` : 'semestral'} de horas trabajadas por cliente
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
              className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Últimos 6 meses</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
            
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-50/50 p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <Loader className="w-10 h-10 text-emerald-600 animate-spin" />
              <p className="text-slate-500 font-medium">Calculando métricas históricas...</p>
            </div>
          ) : error ? (
            <div className="p-6 bg-red-50 border border-red-100 text-red-600 rounded-xl text-center max-w-md mx-auto mt-10">
              <p className="font-medium">{error}</p>
              <button 
                onClick={fetchHistorial}
                className="mt-4 px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
              >
                Reintentar
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] w-64">
                        Cliente
                      </th>
                      {data?.meses.map((mes) => (
                        <th key={mes} className="px-6 py-4 font-semibold text-slate-600 text-center whitespace-nowrap min-w-[120px]">
                          {mes}
                        </th>
                      ))}
                      <th className="px-6 py-4 font-semibold text-emerald-700 bg-emerald-50/30 text-center min-w-[100px]">
                        Promedio
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data?.data.map((row, idx) => {
                      const valores = Object.values(row.historial);
                      const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
                      const maxVal = Math.max(...valores);

                      return (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                          <td className="px-6 py-4 font-medium text-slate-700 sticky left-0 bg-white group-hover:bg-slate-50 transition-colors shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] border-r border-slate-100">
                            {row.cliente}
                          </td>
                          {data.meses.map((mes) => {
                            const horas = row.historial[mes];
                            const horasContratadas = row.horas_contratadas || 0;
                            // Calcular extras solo si hay horas contratadas definidas
                            const horasExtras = horasContratadas > 0 ? Math.max(horas - horasContratadas, 0) : 0;
                            const isMax = horas > 0 && horas === maxVal;
                            
                            let cellContent;
                            if (horas === 0) {
                              cellContent = <span className="text-slate-300">-</span>;
                            } else {
                              cellContent = (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="flex items-center gap-1">
                                    <span className={`font-medium ${
                                      horas > 20 ? 'text-red-600' : 
                                      horas > 5 ? 'text-amber-600' : 
                                      'text-slate-600'
                                    }`}>
                                      {formatHHMM(horas)}
                                    </span>
                                    {horasExtras > 0 && (
                                      <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full border border-red-100" title={`Excedido en ${formatHHMM(horasExtras)}`}>
                                        +{formatHHMM(horasExtras)}
                                      </span>
                                    )}
                                  </div>
                                  {/* Mini bar chart visualization */}
                                  <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${
                                        horas > 20 ? 'bg-red-500' : 
                                        horas > 5 ? 'bg-amber-500' : 
                                        'bg-emerald-500'
                                      }`}
                                      style={{ width: `${Math.min((horas / 25) * 100, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <td key={mes} className={`px-6 py-4 text-center ${isMax ? 'bg-emerald-50/10' : ''}`}>
                                {cellContent}
                              </td>
                            );
                          })}
                          <td className="px-6 py-4 text-center bg-emerald-50/30">
                            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs">
                              <TrendingUp size={12} />
                              {formatHHMM(promedio)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-slate-100 bg-white flex justify-between items-center">
          <div className="flex gap-4 text-xs text-slate-400">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span>Normal (0-5h)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500"></div>
              <span>Alto (5-20h)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span>Crítico ({'>'}20h)</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-900 text-white hover:bg-slate-800 rounded-xl transition-all font-medium shadow-lg shadow-slate-200 hover:shadow-xl active:scale-95"
          >
            Cerrar Panel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
